import type { Provider, TestResult } from "./types";
import { connectionsApi } from "@/lib/api";
import { SmartleadIcon } from "@/components/providers/icons/SmartleadIcon";

/**
 * Smartlead provider implementation with auto-reply support
 */
export const smartleadProvider: Provider = {
  id: "smartlead",
  type: "smartlead",
  name: "Smartlead",
  description:
    "Connect to Smartlead to manage your email outreach and campaigns.",
  icon: SmartleadIcon,
  supportsAutoReply: true,
  configFields: [
    {
      name: "apiKey",
      label: "API Key",
      type: "password",
      required: true,
      placeholder: "Enter your Smartlead API key",
      description: "Find this in your Smartlead account settings",
    },
    {
      name: "workspace",
      label: "Workspace",
      type: "text",
      required: true,
      placeholder: "Enter your workspace name",
    },
    {
      name: "isActive",
      label: "Active",
      type: "toggle",
      required: false,
      description: "Enable or disable this connection",
    },
  ],
  validateConfig: async (config) => {
    if (!config.apiKey || !config.workspace) {
      return false;
    }

    // Validate delay settings if auto-reply is enabled
    if (config.autoReply) {
      const minDelay = config.minReplyDelaySeconds;
      const maxDelay = config.maxReplyDelaySeconds;

      if (minDelay !== undefined && maxDelay !== undefined) {
        if (minDelay < 0 || maxDelay < 0 || minDelay > maxDelay) {
          return false;
        }
      }
    }

    return true;
  },
  testConnection: async (config): Promise<TestResult> => {
    try {
      const result = await connectionsApi.testConnection({
        sourceType: "smartlead",
        apiKey: config.apiKey as string,
        workspace: config.workspace as string,
        isActive: config.isActive as boolean,
        autoReply: config.autoReply as boolean,
        minReplyDelaySeconds: config.minReplyDelaySeconds as number,
        maxReplyDelaySeconds: config.maxReplyDelaySeconds as number,
      });

      return {
        success: result.success,
        message: result.message || "Connection successful",
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Connection failed",
      };
    }
  },
};
